import { List, ListItem, ListItemText } from '@mui/material';
import React from 'react';
import styles from './InvoiceViewLineItemSummary.module.scss';
import rpcShared from '@rockpapercoin/rpc-shared';
import { DocumentNode, gql } from '@apollo/client';
import { InvoiceViewLineItemSummaryInvoiceFragment } from '#types';
import classNames from 'classnames';

export const invoiceViewLineItemSummaryInvoiceFragment = gql`
	fragment invoiceViewLineItemSummaryInvoice on Invoice {
		id
		discountIsPercentage
		discountPercentage
		discountAmount
		lineItems {
			id
			name
			rate
			quantity
			taxPercentage
		}
		organization {
			id
			country
		}
	}
`;

export type InvoiceViewLineItemSummaryProps = {
	invoice?: InvoiceViewLineItemSummaryInvoiceFragment;
	className?: string;
};

type InvoiceViewLineItemSummaryPropsWithFragment =
	React.FC<InvoiceViewLineItemSummaryProps> & {
		fragments: {
			invoiceViewLineItemSummaryInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceViewLineItemSummary: InvoiceViewLineItemSummaryPropsWithFragment =
	( { invoice, className } ) => {
		if ( !invoice ) {
			return null;
		}
		const currencyLabel = rpcShared.invoices.getCurrencyLabel(
			invoice.organization?.country
		);
		const taxTotal =
			rpcShared.invoices.calculateInvoiceTaxTotalToString( invoice );
		const hasDiscountAndIsPercentage =
			invoice.discountIsPercentage &&
			typeof invoice?.discountPercentage === 'number' &&
			invoice.discountPercentage > 0;
		const hasDiscountAndIsAmount =
			!invoice.discountIsPercentage &&
			typeof invoice?.discountAmount === 'number' &&
			invoice.discountAmount > 0;
		const invoiceHasDiscount =
			hasDiscountAndIsPercentage || hasDiscountAndIsAmount;
		return (
			<List
				className={ classNames( styles.container, className ) }
				disablePadding
				data-cy='lineitem-summary'
			>
				<ListItem disablePadding disableGutters>
					<ListItemText>Subtotal</ListItemText>
					<ListItemText data-cy='lineitem-summary-subtotal'>
						{ rpcShared.invoices.calculateInvoiceSubtotalToString(
							invoice.lineItems
						) }
					</ListItemText>
				</ListItem>
				{ invoiceHasDiscount ? (
					<ListItem disablePadding disableGutters>
						<ListItemText data-cy='lineitem-summary-discount-label'>
							Discount
							{ hasDiscountAndIsPercentage ? (
								<> ({ invoice.discountPercentage }%)</>
							) : null }
						</ListItemText>
						<ListItemText data-cy='lineitem-summary-discount'>
							{ rpcShared.invoices.calculateInvoiceDiscountToString( invoice ) }
						</ListItemText>
					</ListItem>
				) : null }
				{ taxTotal ? (
					<ListItem disablePadding disableGutters>
						<ListItemText>Taxes</ListItemText>
						<ListItemText data-cy='lineitem-summary-taxes'>
							{ taxTotal }
						</ListItemText>
					</ListItem>
				) : null }
				<ListItem disablePadding disableGutters className={ styles.strong }>
					<ListItemText>Invoice total{ currencyLabel }</ListItemText>
					<ListItemText data-cy='lineitem-summary-total'>
						{ rpcShared.invoices.calculateInvoiceTotalToString( invoice ) }
					</ListItemText>
				</ListItem>
			</List>
		);
	};

InvoiceViewLineItemSummary.fragments = {
	invoiceViewLineItemSummaryInvoiceFragment,
};
